import React, { useEffect, useState } from 'react';
import { Plus, Edit2, X, AlertCircle, Trash2, Printer } from 'lucide-react';
import { getCOA, createCOA, updateCOA, deleteCOA } from '../../api';
import { useAuth } from '../../AuthContext';

export default function COA() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ kode_akun: '', nama_akun: '', tipe: 'Aset', saldo_normal: 'Debit', deskripsi: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    getCOA().then(res => setData(res.data || [])).catch(err => console.error(err)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ kode_akun: '', nama_akun: '', tipe: 'Aset', saldo_normal: 'Debit', deskripsi: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      kode_akun: item.kode_akun,
      nama_akun: item.nama_akun,
      tipe: item.tipe,
      saldo_normal: item.saldo_normal,
      deskripsi: item.deskripsi || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.kode_akun || !form.nama_akun) { setError('Kode dan nama akun harus diisi'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateCOA(editing.id, form);
      } else {
        await createCOA(form);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus akun ini?')) return;
    try { await deleteCOA(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  const typeColors = {
    Aset: { bg: '#eff6ff', color: '#1e40af' },
    Kewajiban: { bg: '#fef3c7', color: '#92400e' },
    Modal: { bg: '#d1fae5', color: '#065f46' },
    Pendapatan: { bg: '#f0fdf4', color: '#047857' },
    Beban: { bg: '#fee2e2', color: '#991b1b' },
  };

  const generatePDF = () => {
    if (!data.length) return;
    const { jsPDF } = require('jspdf');
    const autoTable = (require('jspdf-autotable').default || require('jspdf-autotable'));
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const now = new Date();

    // Header
    doc.setFillColor(15, 23, 42);
    doc.roundedRect(margin, 12, 26, 16, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    doc.text('LAYANAN', margin + 13, 19, { align: 'center' });
    doc.text('SYNEXA', margin + 13, 24, { align: 'center' });

    doc.setTextColor(15, 23, 42);
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('CHART OF ACCOUNTS', margin + 34, 21);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Daftar Lengkap Akun — Kode, Tipe, dan Saldo Normal', margin + 34, 28);

    // Decorative line
    doc.setDrawColor(15, 23, 42); doc.setLineWidth(0.5);
    doc.line(margin, 32, pageW - margin, 32);

    // Info box
    let y = 36;
    doc.setFillColor(241, 245, 249); doc.roundedRect(margin, y, pageW - 2 * margin, 14, 2, 2, 'F');
    doc.setTextColor(51, 65, 85); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text(`Tanggal Cetak: ${now.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}`, margin + 4, y + 6);
    doc.text(`Total Akun: ${data.length}`, margin + 4, y + 11);

    const typeCounts = {};
    data.forEach(d => { typeCounts[d.tipe] = (typeCounts[d.tipe] || 0) + 1; });
    const summary = Object.entries(typeCounts).map(([k, v]) => `${k}: ${v}`).join('  |  ');
    doc.text(summary, margin + 80, y + 6);

    // Color mapping for tipe
    const tipeColors = {
      Aset: { head: [30, 64, 175], alt: [239, 246, 255] },
      Kewajiban: { head: [146, 64, 14], alt: [254, 243, 199] },
      Modal: { head: [6, 95, 70], alt: [209, 250, 229] },
      Pendapatan: { head: [4, 120, 87], alt: [240, 253, 244] },
      Beban: { head: [153, 27, 27], alt: [254, 226, 226] },
    };

    // Group data by tipe
    const tipeOrder = ['Aset', 'Kewajiban', 'Modal', 'Pendapatan', 'Beban'];
    const grouped = {};
    data.forEach(d => {
      if (!grouped[d.tipe]) grouped[d.tipe] = [];
      grouped[d.tipe].push(d);
    });

    y = 54;

    tipeOrder.forEach(tipe => {
      const items = grouped[tipe];
      if (!items || !items.length) return;
      const tc = tipeColors[tipe] || { head: [51, 65, 85], alt: [248, 250, 252] };

      // Tipe section header bar
      doc.setFillColor(...tc.head);
      doc.roundedRect(margin, y, pageW - 2 * margin, 8, 1, 1, 'F');
      doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
      doc.text(`${tipe} (${items.length} akun)`, margin + 4, y + 5.5);
      y += 10;

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Kode Akun', 'Nama Akun', 'Saldo Normal', 'Deskripsi']],
        body: items.map(d => [d.kode_akun, d.nama_akun, d.saldo_normal, d.deskripsi || '-']),
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, textColor: [30, 41, 59], lineColor: [226, 232, 240], lineWidth: 0.1 },
        headStyles: { fillColor: tc.head, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: tc.alt },
        columnStyles: {
          0: { cellWidth: 25, fontStyle: 'bold' },
          1: { cellWidth: 80 },
          2: { cellWidth: 30, halign: 'center' },
          3: { cellWidth: 'auto' },
        },
      });

      y = doc.lastAutoTable.finalY + 6;
    });

    // Footer on all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(148, 163, 184);
      doc.text('PT Layanan Synexa', margin, doc.internal.pageSize.getHeight() - 8);
      doc.text(`Dicetak: ${now.toLocaleDateString('id-ID')} ${now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}`, pageW - margin, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
    }

    doc.save(`COA_${now.toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Chart of Accounts</h2>
        <p className="page-desc">Daftar akun — kode, tipe, dan saldo normal</p>
      </div>

      <div className="page-header-row" style={{ marginBottom: 16 }}>
        <div>
          <p style={{ fontSize: '0.85rem', color: 'var(--gray-400)' }}>Total {data.length} akun terdaftar</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {data.length > 0 && (
            <button className="btn btn-secondary" onClick={generatePDF} title="Export PDF"><Printer size={16} /><span>Export PDF</span></button>
          )}
          {hasMenuAction('akuntansi', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /><span>Tambah Akun</span></button>
          )}
        </div>
      </div>

      {loading ? <div className="page-loading">Memuat COA...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Kode</th>
                <th>Nama Akun</th>
                <th>Tipe</th>
                <th>Saldo Normal</th>
                <th>Keterangan</th>
                <th style={{ width: 80 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.map(c => {
                const tc = typeColors[c.tipe] || { bg: '#f3f4f6', color: '#374151' };
                return (
                  <tr key={c.id}>
                    <td><strong>{c.kode_akun}</strong></td>
                    <td>{c.nama_akun}</td>
                    <td>
                      <span className="badge" style={{ background: tc.bg, color: tc.color }}>{c.tipe}</span>
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>{c.saldo_normal}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>{c.deskripsi || '-'}</td>
                    <td>
                      <div className="action-btns">
                        {hasMenuAction('akuntansi', 'edit') && (
                          <button className="btn-icon" onClick={() => openEdit(c)}><Edit2 size={14} /></button>
                        )}
                        {hasMenuAction('akuntansi', 'delete') && (
                          <button className="btn-icon btn-danger" onClick={() => handleDelete(c.id)}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Akun' : 'Tambah Akun'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
              <div className="form-grid">
                <div className="form-group">
                  <label>Kode Akun</label>
                  <input type="text" value={form.kode_akun} onChange={e => setForm({...form, kode_akun: e.target.value})}
                    placeholder="Contoh: 1100" disabled={editing} />
                </div>
                <div className="form-group">
                  <label>Nama Akun</label>
                  <input type="text" value={form.nama_akun} onChange={e => setForm({...form, nama_akun: e.target.value})}
                    placeholder="Nama akun" />
                </div>
                <div className="form-group">
                  <label>Tipe</label>
                  <select value={form.tipe} onChange={e => setForm({...form, tipe: e.target.value})}>
                    {['Aset', 'Kewajiban', 'Modal', 'Pendapatan', 'Beban'].map(o =>
                      <option key={o} value={o}>{o}</option>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Saldo Normal</label>
                  <select value={form.saldo_normal} onChange={e => setForm({...form, saldo_normal: e.target.value})}>
                    <option value="Debit">Debit</option>
                    <option value="Kredit">Kredit</option>
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Deskripsi</label>
                  <textarea rows={2} value={form.deskripsi} onChange={e => setForm({...form, deskripsi: e.target.value})}
                    placeholder="Deskripsi akun" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
